import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { adminAPI } from '../../services/api';
import TransactionPasswordModal from '../common/TransactionPasswordModal';
import '../../styles/admin/DeleteBets.css';
import { useDesktopViewport } from '../../hooks/useDesktopViewport';

const DeleteBets = () => {
    useDesktopViewport();
    const { user } = useAuth();
    const [marketId, setMarketId] = useState('');
    const [loading, setLoading] = useState(false);
    const [result, setResult] = useState(null);
    const [showTransactionModal, setShowTransactionModal] = useState(false);
    const [pendingMarketId, setPendingMarketId] = useState('');

    // Check if user is super admin
    const isSuperAdmin = user?.role === 'admin';

    const handleDeleteClick = () => {
        if (!marketId.trim()) {
            setResult({
                success: false,
                message: 'Please enter a Market ID'
            });
            setTimeout(() => setResult(null), 3000);
            return;
        }

        // Show transaction password modal first
        setPendingMarketId(marketId);
        setShowTransactionModal(true);
    };

    const handleTransactionVerified = async (tempToken) => {
        setShowTransactionModal(false);
        localStorage.setItem('transactionToken', tempToken);
        await executeDeleteBets();
    };

    const executeDeleteBets = async () => {
        setLoading(true);
        setResult(null);

        try {
            const response = await adminAPI.deleteUnsettledBets(pendingMarketId);
            
            setResult({
                success: true,
                message: response.message,
                deletedCount: response.data?.deletedCount || 0
            });
            
            // Clear input on success
            setMarketId('');
            
            // Clear result after 3 seconds
            setTimeout(() => setResult(null), 3000);
            
        } catch (err) {
            console.error('Delete error:', err);
            setResult({
                success: false,
                message: err.response?.data?.message || 'Failed to delete bets'
            });
            setTimeout(() => setResult(null), 3000);
        } finally {
            setLoading(false);
            localStorage.removeItem('transactionToken');
            setPendingMarketId('');
        }
    };

    if (!isSuperAdmin) {
        return (
            <div className="delete-bets-container">
                <div className="error-card">
                    <div className="error-icon">⛔</div>
                    <h2>Access Denied</h2>
                    <p>Only Super Admin can access this page.</p>
                </div>
            </div>
        );
    }

    return (
        <div className="delete-bets-container">
            {/* Transaction Password Modal */}
            {showTransactionModal && (
                <TransactionPasswordModal 
                    onVerified={handleTransactionVerified}
                    onClose={() => {
                        setShowTransactionModal(false);
                        setPendingMarketId('');
                    }}
                    action="delete bets"
                />
            )}

            <div className="delete-bets-card">
                <div className="card-header">
                    <div className="header-icon">🗑️</div>
                    <h1>Delete Unsettled Bets</h1>
                    <p>Delete all unsettled bets for a specific market</p>
                </div>

                <div className="card-body">
                    {/* Warning Box */}
                    <div className="warning-box">
                        <div className="warning-icon">⚠️</div>
                        <div className="warning-content">
                            <strong>Warning:</strong>
                            <ul>
                                <li>This will delete ALL unsettled bets for the given market</li>
                                <li>This action cannot be undone</li>
                                <li>Only unsettled bets will be deleted</li>
                                <li>Settled bets will remain unaffected</li>
                                <li>No balance changes will occur</li>
                            </ul>
                        </div>
                    </div>

                    {/* Input Section */}
                    <div className="input-section">
                        <label htmlFor="marketId">Market ID</label>
                        <input
                            type="text"
                            id="marketId"
                            value={marketId}
                            onChange={(e) => setMarketId(e.target.value)}
                            placeholder="Enter Market ID (e.g., 5275892096867)"
                            className="market-input"
                            disabled={loading}
                        />
                        <div className="input-hint">
                            Enter the market ID of the match you want to delete unsettled bets for
                        </div>
                    </div>

                    {/* Result Message */}
                    {result && (
                        <div className={`result-message ${result.success ? 'success' : 'error'}`}>
                            <span className="result-icon">{result.success ? '✅' : '❌'}</span>
                            <div className="result-content">
                                <strong>{result.success ? 'Success!' : 'Error!'}</strong>
                                <p>{result.message}</p>
                                {result.deletedCount > 0 && (
                                    <small>{result.deletedCount} bets deleted</small>
                                )}
                            </div>
                        </div>
                    )}

                    {/* Action Buttons */}
                    <div className="action-buttons">
                        <button
                            onClick={() => setMarketId('')}
                            className="btn-clear"
                            disabled={loading || !marketId}
                        >
                            Clear
                        </button>
                        <button
                            onClick={handleDeleteClick}
                            className="btn-delete"
                            disabled={loading || !marketId.trim()}
                        >
                            {loading ? (
                                <>
                                    <span className="spinner"></span>
                                    Processing...
                                </>
                            ) : (
                                '🗑️ Delete Unsettled Bets'
                            )}
                        </button>
                    </div>
                </div>

                <div className="card-footer">
                    <div className="footer-note">
                        <strong>Note:</strong> This action requires transaction password verification.
                        Only unsettled bets will be deleted. No refunds or balance changes will occur.
                    </div>
                </div>
            </div>
        </div>
    );
};

export default DeleteBets;